import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from "@angular/router";
import { TokenService } from "./token.service";
import { PermissionStore } from "@/pages/service/core/permission-store";

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {

  constructor(
    private tokenService: TokenService,
    private router: Router,
    private perms: PermissionStore
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.check(state);
  }

  /** Runs on EVERY child navigation under the app shell (not just on entry), so a restricted user
   *  can't reach a screen they aren't granted by typing/deep-linking it — the sidebar's allow-list
   *  is enforced at the route too, not merely hidden from the menu. */
  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.check(state);
  }

  private check(state: RouterStateSnapshot): boolean {
    if (!this.tokenService.isLoggedIn()) {
      // No token, or it has expired (24h of inactivity). Drop whatever's left
      // and send them to the login page.
      this.tokenService.clear();
      this.router.navigate(['/auth/login']);
      return false;
    }
    // Hold a user with a default password on the set-password screen until they change it.
    if (this.tokenService.mustChangePassword() && !state.url.startsWith('/set-password')) {
      this.router.navigate(['/set-password']);
      return false;
    }
    // A menu the platform super admin hid for this company can't be reached by URL
    // either — redirect to a safe, unguarded page (the API also rejects it 403).
    if (this.perms.isHidden(state.url)) {
      this.router.navigate(['/notfound']);
      return false;
    }
    // Template / role-grid restriction: a screen the user isn't granted can't be reached by URL
    // either — the sidebar's allow-list, enforced at the route. Unmapped/deep routes stay allowed
    // (canVisit fails open on them), and this is a no-op for unrestricted users + while perms load.
    if (!this.perms.canVisit(state.url)) {
      this.router.navigate(['/notfound']);
      return false;
    }
    return true;
  }
}
